import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import QuotaExceededDialog from './QuotaExceededDialog';
import { BasicDetailsAndType } from './steps/BasicDetailsAndType';
import { TemplateSelection } from './steps/TemplateSelection';
import PasteTemplateDialog from './steps/PasteTemplateDialog';
import { FormTemplate } from './templateConfig';
import { wizardOutlinedButtonSx } from './wizardStyles';
import { formApi } from '../../services';
import './CreateFormWizard.css';

const steps = [
  { title: 'Form Basic Info', subtitle: 'Name your form and choose how it should work.' },
  { title: 'Select a Template', subtitle: 'Choose a pre-built template or start from scratch.' },
];

const DEFAULT_TARGET_DEVICE = 'desktop' as const;

export interface FormData {
  id?: string;
  formName?: string;
  formNameError?: string;
  targetDevice?: 'mobile' | 'tablet' | 'desktop';
  formType?: 'single' | 'multistep';
  template?: any;
  submitEveryStep?: boolean;
}

interface CreateFormDialogProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
}

export const CreateFormDialog: React.FC<CreateFormDialogProps> = ({
  open,
  tenantId,
  onClose,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [submitEveryStep, setSubmitEveryStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    message: string;
    quotaExceeded?: boolean;
  }>({ open: false, message: '' });
  const navigate = useNavigate();

  const resetAndClose = () => {
    setActiveStep(0);
    setFormData({});
    setSubmitEveryStep(false);
    onClose();
  };

  const showError = (message: unknown) => {
    let text: string;
    if (typeof message === 'string') {
      text = message;
    } else if (message && typeof message === 'object') {
      const m = message as any;
      if (m.code === 'QUOTA_EXCEEDED') {
        setErrorDialog({
          open: true,
          message: 'You have reached the maximum number of forms allowed on your plan.',
          quotaExceeded: true,
        });
        return;
      }
      text = m.message || m.error || JSON.stringify(message);
    } else {
      text = 'An unexpected error occurred.';
    }
    setErrorDialog({ open: true, message: text });
  };

  const handleCloseError = () => {
    setErrorDialog({ open: false, message: '', quotaExceeded: false });
  };

  const handleNext = useCallback(
    async (data: Partial<FormData>) => {
      if (submitting) return;
      const updatedData = {
        ...formData,
        ...data,
        targetDevice: formData.targetDevice ?? data.targetDevice ?? DEFAULT_TARGET_DEVICE,
      };
      setFormData(updatedData);

      if (activeStep < steps.length - 1) {
        setActiveStep((prev) => prev + 1);
        return;
      }

      setSubmitting(true);
      try {
        let formId = updatedData.id;

        if (!formId) {
          try {
            const response = await formApi.createForm(tenantId, {
              name: updatedData.formName,
              status: 'draft',
            });
            formId = response.id;
            updatedData.id = formId;
            setFormData(updatedData);
          } catch (error: any) {
            const responseData = error.response?.data;
            const errorMessage =
              (typeof responseData === 'string' ? responseData : null) ||
              error.message ||
              'Error creating form. Please try again.';
            if (
              typeof errorMessage === 'string' &&
              errorMessage.toLowerCase().includes('name already exists')
            ) {
              setFormData({ ...updatedData, formNameError: 'Name already exists' });
              setActiveStep(0);
              return;
            }
            showError(responseData ?? errorMessage);
            return;
          }
        }

        try {
          const formBuilderData = updatedData.template?.configuration || {};
          const updatePayload: any = {
            name: updatedData.formName,
            targetDevice: updatedData.targetDevice,
            formType: updatedData.formType,
            data: formBuilderData,
            submitEveryStep: !!updatedData.submitEveryStep,
          };

          if (updatedData.formType === 'multistep' && updatedData.template?.steps) {
            updatePayload.steps = updatedData.template.steps;
          }

          if (updatedData.template?.theme) {
            updatePayload.theme = updatedData.template.theme;
          }

          await formApi.updateForm(tenantId, formId!, updatePayload);
          resetAndClose();
          navigate(`/tenant/${tenantId}/form/${formId}/edit?device=${updatedData.targetDevice}`);
        } catch (error: any) {
          showError(error.response?.data ?? error.message ?? 'Error saving form. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [activeStep, formData, navigate, tenantId, submitting],
  );

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const pasteFallback = useMemo(
    () => ({
      formType: formData.formType ?? ('single' as const),
      targetDevice: formData.targetDevice ?? DEFAULT_TARGET_DEVICE,
    }),
    [formData.formType, formData.targetDevice],
  );

  const handlePastedTemplate = (template: FormTemplate) => {
    setPasteOpen(false);
    handleNext({ template, formType: template.formType });
  };

  const onToggleSubmitEveryStep = (v: boolean) => setSubmitEveryStep(v);

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : resetAndClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <Box className="wizard-dialog">
          <Box className="wizard-dialog-header">
            <Box>
              <Typography
                className="wizard-dialog-title"
                sx={{ fontSize: '17px !important', fontWeight: '700 !important' }}
              >
                {steps[activeStep].title}
              </Typography>
              <Typography
                className="wizard-dialog-subtitle"
                sx={{ fontSize: '12px !important', fontWeight: '400 !important' }}
              >
                {steps[activeStep].subtitle}
              </Typography>
            </Box>
            <Box className="wizard-dialog-header-actions">
              {activeStep === 1 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentPasteIcon fontSize="small" />}
                  disabled={submitting}
                  onClick={() => setPasteOpen(true)}
                  sx={wizardOutlinedButtonSx}
                >
                  Paste config
                </Button>
              )}
              <IconButton
                onClick={submitting ? undefined : resetAndClose}
                className="wizard-dialog-close"
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box className="wizard-dialog-content">
            {activeStep === 0 && (
              <BasicDetailsAndType
                onNext={handleNext}
                onCancel={resetAndClose}
                initialData={formData}
                loading={submitting}
                submitEveryStep={submitEveryStep}
                onToggleSubmitEveryStep={onToggleSubmitEveryStep}
              />
            )}
            {activeStep === 1 && (
              <TemplateSelection
                onNext={handleNext}
                onBack={handleBack}
                initialData={formData}
                tenantId={tenantId}
                submitting={submitting}
              />
            )}
          </Box>
        </Box>
      </Dialog>

      <PasteTemplateDialog
        open={pasteOpen}
        fallback={pasteFallback}
        onClose={() => setPasteOpen(false)}
        onCreate={handlePastedTemplate}
      />

      <QuotaExceededDialog
        open={errorDialog.open}
        message={errorDialog.message}
        quotaExceeded={errorDialog.quotaExceeded}
        onClose={handleCloseError}
      />
    </>
  );
};

export default CreateFormDialog;
